import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Setup() {
  const { status, loadStatus } = useAuth();
  const navigate = useNavigate();
  const [secret, setSecret] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 10) { setError("Password must be at least 10 characters."); return; }
    setBusy(true);
    try {
      await api.post("/api/auth/setup", { secret, password });
      setDone(true);
      await loadStatus();
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Setup is not available.");
    } finally {
      setBusy(false);
    }
  };

  const configured = status && status.owner_configured;
  const unavailable = status && !status.bootstrap_available && !status.owner_configured;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#121211] px-6 text-[#F9F9F8]">
      <div className="w-full max-w-md">
        <div className="font-display text-3xl tracking-tight">Owner setup</div>
        <p className="mt-2 text-sm text-[#A1A19D]">
          Securely configure the initial owner password. This requires the server-side bootstrap
          secret and can only be done once. No password is ever transmitted in plain support channels.
        </p>

        {configured && (
          <div className="mt-6 border border-[#2C2C2A] bg-[#1A1A18] p-4 text-sm" data-testid="setup-already">
            Owner access is already configured.{" "}
            <Link to="/login" className="text-[#F59E0B] hover:underline">Go to sign in →</Link>
          </div>
        )}

        {unavailable && (
          <div className="mt-6 border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.1)] p-4 text-sm" data-testid="setup-unavailable">
            Setup is not available. The server has no bootstrap secret configured (fail-closed),
            or the owner is already set.
          </div>
        )}

        {!configured && !unavailable && (
          <>
            {done ? (
              <div className="mt-6 border border-[rgba(143,185,122,0.3)] bg-[rgba(143,185,122,0.1)] p-4 text-sm" data-testid="setup-done">
                Owner configured. Redirecting to sign in…
              </div>
            ) : (
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="setup-form">
                <div>
                  <label className="cm-label">Bootstrap secret</label>
                  <input
                    data-testid="setup-secret"
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    className="mt-1 w-full rounded-sm border border-[#2C2C2A] bg-[#1A1A18] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
                    required
                  />
                </div>
                <div>
                  <label className="cm-label">New owner password</label>
                  <input
                    data-testid="setup-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full rounded-sm border border-[#2C2C2A] bg-[#1A1A18] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
                    required
                  />
                </div>
                <div>
                  <label className="cm-label">Confirm password</label>
                  <input
                    data-testid="setup-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="mt-1 w-full rounded-sm border border-[#2C2C2A] bg-[#1A1A18] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
                    required
                  />
                </div>
                {error && <div className="text-sm text-[#e07a7a]" data-testid="setup-error">{error}</div>}
                <button
                  data-testid="setup-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-sm bg-[#F59E0B] px-4 py-2.5 text-sm font-semibold text-[#121211] hover:bg-[#e8940a] disabled:opacity-60"
                >
                  {busy ? "Configuring…" : "Configure owner"}
                </button>
              </form>
            )}
          </>
        )}
        <Link to="/login" className="mt-6 inline-block text-sm text-[#A1A19D] hover:text-[#F9F9F8]">← Back to sign in</Link>
      </div>
    </div>
  );
}
